import type { MetaFunction } from '@remix-run/node'
import { Link } from '@remix-run/react'

import { Code, PANEL, Pill, Section } from '~/components/marketing/DocsKit'

export const meta: MetaFunction = () => [
  { title: 'Docs — Quiver API, CLI & MCP' },
  {
    name: 'description',
    content:
      'Reach into a Quiver workspace from anywhere: a key-authed REST API, the quiver CLI, and a hosted MCP server — all the same backend.',
  },
]

const CONCEPTS: { term: string; body: string }[] = [
  {
    term: 'Workspace',
    body: 'A tenant — your dashboard. Everything (files, metrics, keys) is scoped to one workspace, and a key is bound to exactly one.',
  },
  {
    term: 'File',
    body: 'The primary record (a game, a transaction, a person…). It has a `type` from your config, JSON `data`, and an optional status that flows through phases.',
  },
  {
    term: 'Data field → KPI',
    body: 'Every field you define pushes its current value to a queryable KPI. That’s what powers cross-file metrics — you query aggregates, you don’t write SQL.',
  },
  {
    term: 'Metric',
    body: 'A config-defined aggregate (count, sum, max, grouped breakdowns, derived expressions). Read a computed value over the API; the engine does the math.',
  },
]

export default function DocsOverview() {
  return (
    <div className="space-y-14">
      <section>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-indigo-300">Developer docs</p>
        <h1 className="mt-2 text-4xl font-bold tracking-tight text-white sm:text-5xl">Reach your workspace from anywhere.</h1>
        <p className="mt-5 max-w-2xl text-lg text-zinc-400">
          A Quiver workspace is a live, schema-defined data layer — write records in, read aggregated metrics back out.
          Reach it three ways, all hitting the same API with the same workspace key:
        </p>
        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          {[
            { to: '/docs/api', t: 'REST API', d: 'Key-authed HTTP. Create files, set status & fields, read metrics.' },
            { to: '/docs/cli', t: 'CLI', d: 'The quiver command — scriptable get/set/run over the same API.' },
            { to: '/docs/mcp', t: 'MCP', d: 'Connect Claude or any MCP client; the server self-describes your config.' },
          ].map((c) => (
            <Link key={c.to} to={c.to} className={`${PANEL} block p-5 transition-colors hover:bg-white/[0.04]`}>
              <div className="text-base font-semibold text-zinc-100">{c.t}</div>
              <p className="mt-1.5 text-sm text-zinc-400">{c.d}</p>
              <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-indigo-300">
                Read <span aria-hidden="true">→</span>
              </span>
            </Link>
          ))}
        </div>
        <p className="mt-6 max-w-2xl text-sm text-zinc-500">
          This is the same layer the{' '}
          <a href="https://quiverlabs.ai/archer" className="text-indigo-300 hover:text-indigo-200">
            Quiver Labs archer game
          </a>{' '}
          runs on — a static site that gained persistence, a live leaderboard, and a real-time presence count with no
          backend of its own.
        </p>
      </section>

      <Section eyebrow="Mental model" title="The concepts behind every endpoint">
        <div className="grid gap-4 sm:grid-cols-2">
          {CONCEPTS.map((c) => (
            <div key={c.term} className={`${PANEL} p-5`}>
              <div className="text-sm font-semibold text-zinc-100">{c.term}</div>
              <p className="mt-2 text-sm text-zinc-400">{c.body}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section
        eyebrow="Auth"
        title="Workspace API keys"
        intro={
          <>
            Every door authenticates with a <span className="font-mono text-zinc-300">qvr_</span> workspace key. Mint one
            in <span className="text-zinc-300">Desk → Settings → API keys</span> (shown once — store it like a password),
            or from the CLI with <span className="font-mono text-zinc-300">quiver workspace apikey create</span>.
          </>
        }
      >
        <div className={`${PANEL} p-5`}>
          <div className="flex flex-wrap items-center gap-2">
            <Pill tone="indigo">Bearer auth</Pill>
            <Pill>read vs. write</Pill>
          </div>
          <p className="mt-3 text-sm text-zinc-400">
            Pass the key as a bearer token on every request. A <span className="font-mono text-zinc-300">qvr_</span> key
            has full access to its workspace; <span className="font-mono">GET</span> requests are reads,{' '}
            <span className="font-mono">POST/PATCH/DELETE</span> are writes. (OAuth access tokens can be narrowed to a
            read-only scope — see the API page.)
          </p>
          <div className="mt-4">
            <Code label="every request">{`Authorization: Bearer qvr_xxxxxxxxxxxx`}</Code>
          </div>
        </div>
      </Section>

      <Section eyebrow="Quickstart" title="A 60-second round-trip" intro="Create a record, then read a metric back — the whole loop, over curl.">
        <Code label="base url · https://quiveros.ai">{`# 1 — create a file (a "score" in a leaderboard workspace)
curl -X POST https://quiveros.ai/api/files \\
  -H "Authorization: Bearer $QUIVER_KEY" \\
  -H "content-type: application/json" \\
  -d '{"type":"score","title":"Alice · 250","data":{"player":"Alice","value":250,"category":"hard"}}'
# → { "ok": true, "file": { "id": "…", "type": "score", "title": "Alice · 250" } }

# 2 — read a computed metric back
curl "https://quiveros.ai/api/metrics?id=leaderboard" \\
  -H "Authorization: Bearer $QUIVER_KEY"
# → { "metric": { "id": "leaderboard", "metric": 250, "breakdown": [ … ] } }`}</Code>
        <p className="text-sm text-zinc-500">
          Same key, same endpoints from the <Link to="/docs/cli" className="text-indigo-300 hover:text-indigo-200">CLI</Link>{' '}
          or an <Link to="/docs/mcp" className="text-indigo-300 hover:text-indigo-200">MCP client</Link>. Pick the door
          that fits how you build.
        </p>
      </Section>
    </div>
  )
}
